import clsx from 'clsx';
import React, { useContext, useMemo } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';
import { AlertManager } from '../../context/AlertManager';
import Loading from '../UpdateCardWidget/Loading';
import Divider from '../atoms/divider';
import Alert from '../molecules/alert';

type RouteDefinition = {
  path: string;
  label: string;
  content: React.FC;
};

type Props = {
  routes: RouteDefinition[];
};

const titleClassName =
  'not-italic text-xl leading-9 flex text-[#161616] mr-1 mb-1';

const PageTitle: React.FC = ({ children }) => (
  <h1 className={clsx(titleClassName, 'font-bold')}>{children}</h1>
);

const PageSubTitle: React.FC = ({ children }) => (
  <h1 className={clsx(titleClassName, 'font-normal')}>{children}</h1>
);

type SideNavMenuItemProps = {
  item: RouteDefinition;
  selected: boolean;
};
const SideNavMenuItem: React.FC<SideNavMenuItemProps> = ({
  item,
  selected,
}) => {
  const { push } = useHistory();
  return (
    <div
      className={clsx(
        'mb-2 flex w-full flex-1 content-center rounded-[10px] px-4 py-2 text-dim-300 no-underline transition-all duration-300 ease-[ease] hover:bg-[#30c594] hover:text-white',
        selected ? 'bg-[#f0f0f0] font-bold' : 'bg-transparent font-normal',
        'cursor-pointer',
      )}
      onClick={() => push(item.path)}
    >
      {item.label}
    </div>
  );
};

const prefixWith = (path: string) => (route: RouteDefinition) => ({
  ...route,
  path: path + route.path,
});

type EnterpriseDashboardPageProps = {
  title: string;
  subTitle: string;
  toolbar?: React.ReactElement;
  loading?: boolean;
};

export const EnterpriseDashboardPage: React.FC<
  EnterpriseDashboardPageProps
> = ({ title, subTitle, toolbar, loading, children }) => (
  <>
    <div className="sticky top-[60px] bg-white pt-6">
      <div className="mb-2 flex h-10 flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          <PageTitle>{title}</PageTitle>
          <PageSubTitle>{subTitle}</PageSubTitle>
        </div>
        <div className="flex self-end">{toolbar}</div>
      </div>
      <Divider />
    </div>
    {loading ? <Loading /> : <div className="pb-10">{children}</div>}
  </>
);

export const EnterpriseDashboardLayout: React.FC<Props> = ({ routes }) => {
  const { alert, setAlert } = useContext(AlertManager);
  const { url, path: rootPath } = useRouteMatch();
  const firstPath = routes[0].path;
  const pathname = useHistory().location.pathname;
  const activePath = pathname.replace(url, '') ?? firstPath;
  const rootAwareSubRoutes = useMemo(
    () => routes.map(prefixWith(rootPath)),
    [rootPath, routes],
  );

  return (
    <section className="min-h-[75.8vh] bg-white p-0">
      {alert && (
        <div className="p-4">
          <Alert
            title={alert.title}
            severity={alert.severity}
            onClose={() => setAlert(undefined)}
          >
            {alert.message}
          </Alert>
        </div>
      )}
      <div className="flex flex-1 flex-row items-stretch">
        <section className="relative flex w-56 flex-row flex-nowrap bg-inherit px-5 py-6 !text-left">
          <div className="block w-full">
            <div className="sticky top-[84px]">
              {routes.map((route) => (
                <SideNavMenuItem
                  key={route.path}
                  item={prefixWith(rootPath)(route)}
                  selected={route.path === activePath}
                />
              ))}
            </div>
          </div>
        </section>
        <section className="flex-1 bg-inherit px-5 pb-6 !text-left [&>*]:mb-2">
          <Switch>
            {rootAwareSubRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                component={route.content}
              />
            ))}
            <Route
              path=""
              component={() => <Redirect to={rootAwareSubRoutes[0].path} />}
            />
          </Switch>
        </section>
      </div>
    </section>
  );
};
